// Obs: the xlsx library is big. Make sure to lazy load components that use helpers from this file.
import { WorkBook as XLSXWorkBook, read, utils } from 'xlsx';

import { transpose } from './matrix';

export type WorkBook = XLSXWorkBook;

/**
 * Reads a selected xlsx or CSV file and returns a workbook in an intermediate format.
 * Copied from E1
 *
 * This function reads the first 1000 rows of the first file from a FileList, if it exists,
 * and rejects with an error otherwise. Errors are also thrown if there's an issue reading or processing the file.
 *
 * @param {FileList | null} files - A FileList object representing the selected files.
 *                                  It expects only the first file to be processed.
 * @returns {Promise<WorkBook | null>} A Promise that resolves to a WorkBook object in intermediate format.
 *                                     The Promise can also reject with an error.
 * @throws Will throw an error if no files are provided or if reading or processing fails.
 */
export const readFile = (files: FileList | null) => {
  const reader = new FileReader();
  // Promise for returning the parsed data once the reader loads, and we get the parsed data
  return new Promise((resolve, reject) => {
    if (!files || !files.length) reject(null);

    try {
      // when the reader loads
      reader.onerror = (e) => reject(e);
      reader.onload = (e) => {
        try {
          // read the first 1000 lines into the intermediate format
          // truncate the output at 1000 lines.

          const workbook = read(e!.target!.result, { type: 'binary', sheetRows: 1000 });

          resolve(workbook);
        } catch (error) {
          reject(error);
        }
      };

      // read the first file selected
      reader.readAsBinaryString(files![0]);
    } catch (error) {
      // if we catch any errors, reject the result with the message, which will log to console.error on return.
      reject(error);
    }
  });
};

/**
 * Checks if a cell is populated with meaningful data.
 *
 * @param {string | undefined | null} cell - The cell data.
 * @returns {boolean} `true` if the cell is filled with meaningful data, otherwise `false`.
 */
const isCellFilled = (cell: string | undefined | null): boolean => cell !== undefined && cell !== null && cell !== '';

/**
 * Helper function to find the index of the first row that satisfies the given condition.
 *
 * @param {Array[]} data - Two-dimensional array of sheet data.
 * @param {(row: any[]) => boolean} condition - The condition function to test the row.
 * @returns {number} The index of the first row that satisfies the condition.
 */
const findRowIndex = (data: any[], condition: (row: any[]) => boolean): number => {
  for (let i = 0; i < data.length; i++) {
    const row = data[i];
    if (row === undefined || row.every((cell: string | undefined) => cell === undefined || cell === '')) {
      continue; // Skip rows that are entirely empty or undefined
    }
    if (condition(row)) return i;
  }
  return -1;
};

/**
 * Count the number of empty rows from the top of the data.
 *
 * @param {Array[]} data - Two-dimensional array of sheet data.
 * @returns {number} The count of empty rows from the top.
 */
const countEmptyRowsFromTop = (data: unknown[]): number => {
  const index = findRowIndex(data, (row) => row.some(isCellFilled));
  return index === -1 ? data.length : index;
};

/**
 * Count the number of empty rows from the bottom of the data.
 *
 * @param {Array[]} data - Two-dimensional array of sheet data.
 * @returns {number} The count of empty rows from the bottom.
 */
const countEmptyRowsFromBottom = (data: string[]): number => {
  const reversedData = data.slice().reverse(); // Create a shallow copy and reverse it
  const index = findRowIndex(reversedData, (row) => row.some(isCellFilled));
  return index === -1 ? data.length : index;
};

/**
 * Remove empty rows from the top and bottom of the provided sheet data.
 *
 * @param {Array[]} data - Two-dimensional array of sheet data.
 * @returns {Array[]} The sheet data with empty rows removed.
 */
const removeEmptyRows = (data: string[]): string[] => {
  const emptyRowCountTop = countEmptyRowsFromTop(data);
  const emptyRowCountBottom = countEmptyRowsFromBottom(data);
  return data.filter((_, index) => index >= emptyRowCountTop && index < data.length - emptyRowCountBottom);
};

/**
 * Count the number of empty columns from the left of the data.
 *
 * @param {Array[]} data - Two-dimensional array of sheet data.
 * @returns {number} The count of empty columns from the left.
 */
const countEmptyColumnsFromLeft = (data: number[][]): number => {
  const transposedData = transpose(data);
  const emptyRowsCount = countEmptyRowsFromTop(transposedData);
  return emptyRowsCount;
};

/**
 * Remove empty columns from the left of the provided sheet data.
 *
 * @param {Array[]} data - Two-dimensional array of sheet data.
 * @returns {Array[]} The sheet data with empty columns removed.
 */
const removeEmptyColumns = (data: number[][]): number[][] => {
  const emptyColumnCount = countEmptyColumnsFromLeft(data);
  return data.map((row) => row.slice(emptyColumnCount));
};

/**
 * Removes empty rows and columns from the top, left and bottom of the provided sheet data.
 *
 * @param {Array[]} data - Two-dimensional array of sheet data.
 * @returns {Array[]} The trimmed sheet data.
 *
 * @throws {Error} If `data` is not an array.
 *
 * @example
 * // data:
 * // [
 * //   ['', '', ''],
 * //   ['', 'A', 'B'],
 * //   ['', 'C', 'D'],
 * //   ['', '', ''],
 * // ]
 * //
 * // returns:
 * // [
 * //   ['A', 'B'],
 * //   ['C', 'D'],
 * // ]
 */
export const trimEmptyData = (data: any[]) => {
  if (!Array.isArray(data)) throw Error('"data" must be an array');
  if (!data.length) return [];

  data = removeEmptyRows(data);
  data = removeEmptyColumns(data);

  return data;
};

/**
 * Converts a sheet in an Excel WorkBook to a 2D array of strings.
 *
 * @param {WorkBook} workBook - The Excel WorkBook containing the sheet.
 * @param {string} sheetName - The name of the sheet in the WorkBook to convert.
 *
 *  @returns {string[][] | undefined} A 2D array of strings representing the sheet data or undefined if something went wrong.
 */
export const convertToArray = (workBook: WorkBook, sheetName: string): string[][] | undefined => {
  if (!workBook?.Sheets?.[sheetName]) {
    console.error(`Sheet ${sheetName} not found in workbook.`);
    return;
  }

  // output a 2D array instead of an array of objects
  // do not attempt converting data types, keep as strings
  let result = utils.sheet_to_json(workBook.Sheets[sheetName], { header: 1, raw: false, blankrows: true, defval: '' });
  result = trimEmptyData(result);

  // if we have 1000 rows, it was probably truncated, so log it.
  if (result.length === 1000) console.error('Exceeded 1000 row limit.');

  return result as string[][];
};

/**
 * Convert a 2D array of strings to a CSV string.
 * @param {string[][]} sheetData - The 2D array of strings to convert.
 * @returns {string} The CSV string.
 */
export const convertToCSV = (sheetData: string[][]): string => {
  if (!sheetData) return '';

  return sheetData.map((row) => row.join(',')).join('\n');
};
